<template>
  <OurCard class="card" data-cy="login-page">
    <OurHeader class="login-title" size="lg" weight="bold" :text="$t('page.login.title')" />

    <OurGroup size="lg">
      <OurInput
        v-model="form.email"
        data-cy="email-input"
        :label="$t('label.email')"
        :placeholder="$t('placeholder.email')"
        :error="emailError"
        :size="inputSize"
      />

      <OurInput
        v-model="form.password"
        data-cy="password-input"
        :label="$t('label.password._')"
        :placeholder="$t('placeholder.password')"
        :error="passwordError"
        type="password"
        :size="inputSize"
      />
    </OurGroup>

    <div>
      <OurButton data-cy="submit-btn" :text="$t('button.login')" size="lg" @click="onSubmitForm" />

      <div v-if="isDealerLogin" class="align-center mt-5 flex justify-center gap-2">
        <span>{{ $t("page.login.withoutProfile") }}</span>
        <OurLink
          data-cy="forgot-password"
          class="font-bold underline underline-offset-4"
          :text="$t('page.login.registration')"
          :route="registrationRoute"
          variant="secondary"
          size="md"
          router-link
        />
      </div>

      <OurLink
        v-if="!isProduction"
        data-cy="forgot-password"
        class="auth-link"
        :text="$t('page.login.forgotPassword')"
        :route="forgotPasswordRoute"
        variant="secondary"
        size="sm"
        router-link
      />
    </div>
  </OurCard>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { required, email, minLength } from "vuelidate/lib/validators";
import ValidationService from "@/services/validation.service";
import { USER_TYPE } from "@/api/user";

const { dealer, employee } = USER_TYPE;
const { PASSWORD_MIN_LENGTH } = ValidationService;

export default {
  name: "Login",

  data: () => ({
    form: {
      email: "",
      password: "",
      userType: "", //dealer or employee
    },
  }),

  validations: {
    form: {
      email: { required, email },
      password: { required, minLength: minLength(PASSWORD_MIN_LENGTH) },
    },
  },

  computed: {
    ...mapGetters("breakpoint", ["inputSize"]),

    isProduction() {
      return process.env.VUE_APP_FEATURE_FLAG_LEGACY === "1";
    },

    isDealerLogin() {
      return this.$route.name === "Login";
    },

    emailError() {
      const isDirty = this.$v.form.email.$dirty;
      const isFilled = this.$v.form.email.required;
      const isEmail = this.$v.form.email.email;

      let error = "";

      if (isDirty && !isFilled) {
        error = this.$t("validation.required");
      } else if (isDirty && !isEmail) {
        error = this.$t("validation.mustBeEmail");
      }

      return error;
    },

    passwordError() {
      const isDirty = this.$v.form.password.$dirty;
      const isFilled = this.$v.form.password.required;
      const minLength = this.$v.form.password.minLength;

      let error = "";

      if (isDirty && !isFilled) {
        error = this.$t("validation.required");
      } else if (isDirty && !minLength) {
        error = this.$t("validation.passwordMinLength", {
          length: PASSWORD_MIN_LENGTH,
        });
      }

      return error;
    },

    forgotPasswordRoute() {
      const routerName = this.isDealerLogin ? "ForgotPassword" : "AdminForgotPassword";

      return { name: routerName };
    },

    registrationRoute() {
      return { name: "Registration" };
    },
  },

  created() {
    this.form.userType = this.isDealerLogin ? dealer : employee;
  },

  methods: {
    ...mapMutations("user", ["SAVE_USER_DEALER_STATE"]),
    ...mapActions(["login"]),

    async onSubmitForm() {
      if (this.$v.$invalid) {
        this.$v.$touch();

        return;
      }

      const formData = {
        email: this.form.email,
        password: this.form.password,
        userType: this.form.userType,
        rememberMe: true,
      };

      await this.login(formData);
      await this.SAVE_USER_DEALER_STATE(this.form.userType);

      window.location.replace("/");
    },
  },
};
</script>

<style lang="postcss" scoped>
.login-title {
  @apply text-center;
  @apply mb-6 md:mb-8;
}

.login-wrapper-link {
  @apply mt-6 justify-center;
}

.login-link {
  @apply text-sm font-normal text-gray-500 underline;
}

@media (max-width: 768px) {
  .card.our-card:deep {
    min-width: 100%;
    border: unset;
  }
}
</style>
